import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

/**
 * THE identity localStorage write — `sfx_user_id` and the display keys beside
 * it — from a `/api/auth/*` user payload (`buildUserResponse` in
 * `server/routes/auth.js`, which always carries `id`).
 *
 * 🚨 IT EXISTS BECAUSE A SESSION CAN CHANGE WHILE THE APP STAYS MOUNTED, AND
 *    `sfx_user_id` IS WHAT ~15 SURFACES USE TO ANSWER "WHO AM I".
 * `POST /api/auth/impersonate` and `POST /api/auth/exit-impersonate` both swap
 * the session cookie server-side and then assign `window.location.href`.
 * **Assigning `href` starts a navigation; it does not stop the page.** Timers
 * keep firing until the new document commits, so for the whole of that window
 * the cookie belongs to one user and `localStorage.sfx_user_id` to another.
 * The bell (`components/Layout.js`) polls
 * `GET /api/notifications/user/${localStorage.sfx_user_id}` on the shared
 * `POLL_INTERVAL_MS`, and that route refuses a non-admin asking for somebody
 * else — so a tick landing in that window emits a 403 recorded against the
 * impersonated user. Measured on production 2026-09-19, when each of the TWO
 * bell instances still polled for itself (since 2026-09-24 both share ONE
 * count-only poll): two rows for Jake Dimmer, two for Elsie Ray-Leary, both
 * for `/api/notifications/user/1`.
 *
 * 🔑 THE MOUNT PATH IS NOT THE HAZARD AND DOES NOT NEED A GATE. `Layout`
 * renders only inside `RequireAuth` (`App.js`), which holds a spinner while
 * `loading` is true, and `refreshAuth` writes these keys BEFORE its `finally`
 * clears `loading` — so the bell cannot mount before identity is known.
 *
 * ⚠️ `signup` deliberately does NOT call this: it writes only the three
 * required keys and no email/phone, so routing it here would ADD an
 * `sfx_buyer_email` write at signup — and an `sfx_user_phone` one too, were a
 * phone ever present — a behaviour change, not a tidy-up.
 * `login` DOES call it: its former inline copy was identical in effect (its
 * `data.user.phone || ''` sat inside `if (data.user.phone)`, so the `|| ''`
 * was dead code). An earlier revision of this docblock claimed login had
 * "slightly different email/phone handling" and that was simply false —
 * corrected 2026-09-19 after review.
 *
 * 🪤 The `user.id == null` guard is DEFENCE IN DEPTH, not behaviour: all four
 * callers receive `buildUserResponse` output (`server/routes/auth.js`), whose
 * `id` is the primary key, so the early return is unreachable on any 200.
 * Do not read it as evidence that a null id is expected here.
 */
function syncIdentityStorage(user) {
  if (!user || user.id == null) return;
  localStorage.setItem('sfx_user_id', String(user.id));
  localStorage.setItem('sfx_requested_by', user.display_name);
  localStorage.setItem('sfx_user_role', user.role);
  if (user.email) localStorage.setItem('sfx_buyer_email', user.email);
  if (user.phone) localStorage.setItem('sfx_user_phone', user.phone);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const authenticated = !!user;

  /** Fetch current session from server */
  const refreshAuth = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (!res.ok) {
        setUser(null);
        setImpersonating(false);
        setOriginalUser(null);
        return;
      }
      const data = await res.json();
      if (data.authenticated) {
        setUser(data.user);
        setImpersonating(!!data.impersonating);
        setOriginalUser(data.originalUser || null);
        setMustChangePassword(!!data.user.mustChangePassword);

        // Sync localStorage keys that other components still read
        syncIdentityStorage(data.user);
      } else {
        setUser(null);
        setImpersonating(false);
        setOriginalUser(null);
        setMustChangePassword(false);
      }
    } catch (err) {
      console.error('[AuthContext] Failed to fetch auth state:', err);
      setUser(null);
      setMustChangePassword(false);
    } finally {
      setLoading(false);
    }
  }, []);

  /** Log in with email + password */
  const login = useCallback(async (email, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Login failed');
    }
    setUser(data.user);
    setMustChangePassword(!!data.user.mustChangePassword);
    // Sync localStorage — one identity writer, shared with refreshAuth,
    // impersonate and exitImpersonation. The inline copy this replaced was
    // identical in effect; see `syncIdentityStorage`'s docblock.
    syncIdentityStorage(data.user);
    window.dispatchEvent(new Event('sfx-role-change'));
    return data.user;
  }, []);

  /** Sign up a new user */
  const signup = useCallback(async (display_name, email, password) => {
    const res = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ display_name, email, password }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Signup failed');
    }
    setUser(data.user);
    localStorage.setItem('sfx_user_id', String(data.user.id));
    localStorage.setItem('sfx_requested_by', data.user.display_name);
    localStorage.setItem('sfx_user_role', data.user.role);
    window.dispatchEvent(new Event('sfx-role-change'));
    return data.user;
  }, []);

  /** Log out */
  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      // Ignore network errors on logout
    }
    setUser(null);
    setImpersonating(false);
    setOriginalUser(null);
    setMustChangePassword(false);
    // Clear identity localStorage
    localStorage.removeItem('sfx_user_id');
    localStorage.removeItem('sfx_requested_by');
    localStorage.removeItem('sfx_user_role');
    localStorage.removeItem('sfx_active_account');
    localStorage.removeItem('sfx_active_department_id');
    // 🔑 sessionStorage, and it must be cleared HERE as well as expiring with
    // the tab. A browser session outlives a PRODUCT session: logging out and
    // back in — as the same person or as somebody else — reuses the tab, so
    // without this the next user inherits the previous one's "yes, put me in
    // My Space" and lands somewhere they never chose. The owner's rule is
    // *"every login will default to the default department"*, and a login is
    // exactly the boundary this crosses.
    try { sessionStorage.removeItem('sfx_myspace_session_choice'); } catch { /* storage unavailable */ }
  }, []);

  /** Admin: impersonate another user */
  const impersonate = useCallback(async (targetUserId) => {
    const res = await fetch('/api/auth/impersonate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ userId: targetUserId }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Impersonation failed');
    }
    // 🔑 THE LOAD-BEARING PROPERTY IS "NO `await` BETWEEN THE BODY PARSE AND
    // THIS WRITE" — not "before the `href` assignment below". The cookie is
    // already the target's the moment the response headers land, and this page
    // keeps polling until the new document commits; JS runs to completion, so
    // once `res.json()` has resolved nothing can interleave before this line.
    // Moving it below the `href` assignment would be equally correct — by that
    // reasoning, NOT by the suite, which cannot distinguish the two (the
    // mutation stays green). Adding an `await` above it would NOT be correct.
    // See `syncIdentityStorage`'s docblock.
    syncIdentityStorage(data.user);
    // Reload page to re-hydrate all contexts with new session
    window.location.href = '/';
  }, []);

  /** Exit impersonation, return to admin session */
  const exitImpersonation = useCallback(async () => {
    const res = await fetch('/api/auth/exit-impersonate', {
      method: 'POST',
      credentials: 'include',
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Failed to exit impersonation');
    }
    // The mirror of the line in `impersonate`. This direction does not produce
    // the 403 (the restored admin passes the notifications gate for anybody),
    // but leaving the impersonated id behind would mis-identify the admin on
    // every OTHER `sfx_user_id` reader for the same window.
    syncIdentityStorage(data.user);
    // Reload to restore admin context
    window.location.href = '/admin/dashboard';
  }, []);

  // Hydrate auth state on mount
  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const value = {
    user,
    loading,
    authenticated,
    impersonating,
    originalUser,
    mustChangePassword,
    setMustChangePassword,
    login,
    signup,
    logout,
    refreshAuth,
    impersonate,
    exitImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
